/* Printhia Bundle Configurator.
 *
 * The catalogue is already in the DOM (server rendered by the section), so this
 * file only owns selection state, the ladder maths and the cart hand-off.
 *
 * Ladder source: the `printbundles.pricing` metafield the section printed into
 * [data-pbb-pricing]. Nothing here invents a discount, so the page can only
 * promise what the PrintBundles function actually applies at checkout.
 *
 * Cart hand-off: a plain window.fetch POST to /cart/add.js. pb-cart.js wraps
 * window.fetch and opens the drawer on any successful /cart/add match, so the
 * drawer needs no extra wiring here.
 */
(function () {
  "use strict";

  var MAX_CARDS_SCAN = 5000;

  function money(cents, format) {
    var value = (cents || 0) / 100;
    function withSep(v, decimals, thousands, decimal) {
      var fixed = v.toFixed(decimals);
      var parts = fixed.split(".");
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return parts.length > 1 ? parts.join(decimal) : parts[0];
    }
    // Mirrors the theme's money_format so the page reads like the rest of the
    // store instead of inventing its own currency style.
    return String(format || "{{amount}}").replace(
      /\{\{\s*(\w+)\s*\}\}/g,
      function (_, name) {
        switch (name) {
          case "amount":
            return withSep(value, 2, ",", ".");
          case "amount_no_decimals":
            return withSep(value, 0, ",", ".");
          case "amount_with_comma_separator":
            return withSep(value, 2, ".", ",");
          case "amount_no_decimals_with_comma_separator":
            return withSep(value, 0, ".", ",");
          case "amount_with_apostrophe_separator":
            return withSep(value, 2, "'", ".");
          case "amount_no_decimals_with_space_separator":
            return withSep(value, 0, " ", ",");
          case "amount_with_space_separator":
            return withSep(value, 2, " ", ",");
          default:
            return withSep(value, 2, ",", ".");
        }
      },
    );
  }

  // Slovak needs three forms: 1 kus, 2-4 kusy, 5+ kusov.
  function plural(n, forms) {
    if (!forms || forms.length < 3) return "";
    if (n === 1) return forms[0];
    if (n >= 2 && n <= 4) return forms[1];
    return forms[2];
  }

  function parseJSON(el, fallback) {
    if (!el) return fallback;
    try {
      var parsed = JSON.parse(el.textContent || "null");
      return parsed === null || parsed === undefined ? fallback : parsed;
    } catch (e) {
      return fallback;
    }
  }

  function init(root) {
    if (!root || root.__pbbReady) return;
    root.__pbbReady = true;

    var moneyFormat = root.getAttribute("data-money-format") || "{{amount}}";
    var maxQty = parseInt(root.getAttribute("data-max-qty"), 10) || 20;
    var addUrl = root.getAttribute("data-cart-add-url") || "/cart/add";
    var pricing = parseJSON(root.querySelector("[data-pbb-pricing]"), null);
    var S = parseJSON(root.querySelector("[data-pbb-strings]"), {});

    var cards = [].slice.call(
      root.querySelectorAll("[data-pbb-card]"),
      0,
      MAX_CARDS_SCAN,
    );
    if (!cards.length) return;

    // ---- Ladder -----------------------------------------------------------
    // Tuples are [minQty, kind, value]; only percent and fixed-amount steps can
    // be priced on the page. Anything else (free units) still adds to the cart,
    // it just does not draw a tier card.
    var ladder = [];
    if (pricing && Object.prototype.toString.call(pricing.A) === "[object Array]") {
      ladder = pricing.A.filter(function (t) {
        return (
          Object.prototype.toString.call(t) === "[object Array]" &&
          typeof t[0] === "number" &&
          (t[1] === "p" || t[1] === "x")
        );
      }).sort(function (a, b) {
        return a[0] - b[0];
      });
    }
    var grouped = !!(pricing && pricing.g);

    function tierFor(qty) {
      var best = null;
      for (var i = 0; i < ladder.length; i++) {
        if (qty >= ladder[i][0]) best = ladder[i];
      }
      return best;
    }

    function discountCents(subtotal, tier) {
      if (!tier) return 0;
      if (tier[1] === "p") return Math.round((subtotal * tier[2]) / 100);
      if (tier[1] === "x") return Math.min(subtotal, Math.round(tier[2] * 100));
      return 0;
    }

    // Reference unit price for the tier cards: the most common price in the
    // grid. If the collection is not uniformly priced the cards say "od".
    var priceCounts = {};
    var topPrice = 0;
    var topCount = 0;
    var distinct = 0;
    cards.forEach(function (card) {
      var p = parseInt(card.getAttribute("data-price"), 10) || 0;
      if (!priceCounts[p]) {
        priceCounts[p] = 0;
        distinct++;
      }
      priceCounts[p]++;
      if (priceCounts[p] > topCount) {
        topCount = priceCounts[p];
        topPrice = p;
      }
    });
    var mixedPricing = distinct > 1;

    // ---- State ------------------------------------------------------------
    var sel = Object.create(null); // vid -> qty
    var order = []; // insertion order of vids
    var byId = Object.create(null);
    cards.forEach(function (card) {
      byId[card.getAttribute("data-vid")] = card;
    });

    var filter = "*";
    var query = "";
    var toastTimer = null;
    // Eligible items ALREADY in the cart: they count toward the ladder tier,
    // because the discount function sums cart lines, not page selections.
    var cartQty = 0;

    function syncCartQty(cb) {
      var responseStatus;
      window
        .fetch("/cart.js", { headers: { Accept: "application/json" } })
        .then(function (res) {
          responseStatus = res.status;
          if (!res.ok) throw new Error('Cart read failed');
          return res.json();
        })
        .then(function (cart) {
          if (!cart || !Array.isArray(cart.items) || typeof cart.item_count !== 'number') throw new Error('Cart response invalid');
          var n = 0;
          (cart.items || []).forEach(function (it) {
            if (byId[String(it.variant_id)]) n += it.quantity;
          });
          cartQty = n;
          if (typeof cart.item_count === "number") updateBubble(cart.item_count);
          if (cb) cb(cart);
          render();
        })
        .catch(function () {
          var message = root.querySelector('[data-pbb-error]');
          if (!message) { message = document.createElement('p'); message.setAttribute('data-pbb-error', ''); message.setAttribute('role', 'alert'); root.appendChild(message); }
          message.hidden = false;
          message.textContent = 'Údaje košíka sa nepodarilo načítať. Pred odoslaním skontrolujte jeho obsah.';
          if (window.PrinthiaErrors) window.PrinthiaErrors.report({code:'cart_read_failed',flow:'cart',endpoint:'/cart.js',status:responseStatus>=400?responseStatus:undefined,show:false});
          if (cb) cb(null);
        });
    }

    var el = {
      tiers: root.querySelector("[data-pbb-tiers]"),
      chips: root.querySelector("[data-pbb-chips]"),
      search: root.querySelector("[data-pbb-search]"),
      grid: root.querySelector("[data-pbb-grid]"),
      gridCount: root.querySelector("[data-pbb-grid-count]"),
      noResults: root.querySelector("[data-pbb-noresults]"),
      counter: root.querySelector("[data-pbb-counter]"),
      progress: root.querySelector("[data-pbb-progress]"),
      hint: root.querySelector("[data-pbb-hint]"),
      toast: root.querySelector("[data-pbb-toast]"),
      drop: root.querySelector("[data-pbb-drop]"),
      bar: root.querySelector("[data-pbb-bar]"),
      barCount: root.querySelector("[data-pbb-bar-count]"),
      barTotal: root.querySelector("[data-pbb-bar-total]"),
      barSave: root.querySelector("[data-pbb-bar-save]"),
      sheet: root.querySelector("[data-pbb-sheet]"),
      sub: root.querySelector("[data-pbb-sub]"),
      addCategory: root.querySelector("[data-pbb-add-category]"),
      mystery: root.querySelector("[data-pbb-mystery]"),
    };
    var chosenLists = [].slice.call(root.querySelectorAll("[data-pbb-chosen]"));
    var totalNodes = [].slice.call(root.querySelectorAll("[data-pbb-total]"));
    var submits = [].slice.call(root.querySelectorAll("[data-pbb-submit]"));
    var saveRows = [].slice.call(root.querySelectorAll("[data-pbb-save-row]"));

    function count() {
      var n = 0;
      for (var k in sel) n += sel[k];
      return n;
    }

    function subtotalCents() {
      var sum = 0;
      for (var k in sel) {
        var card = byId[k];
        if (card) sum += (parseInt(card.getAttribute("data-price"), 10) || 0) * sel[k];
      }
      return sum;
    }

    // ---- Toast ------------------------------------------------------------
    function toast(msg) {
      if (!el.toast || !msg) return;
      clearTimeout(toastTimer);
      el.toast.textContent = msg;
      el.toast.hidden = false;
      // Restart the CSS animation on repeat messages.
      el.toast.style.animation = "none";
      void el.toast.offsetWidth;
      el.toast.style.animation = "";
      toastTimer = setTimeout(function () {
        el.toast.hidden = true;
      }, 2600);
    }

    var reduceMotion =
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    function fly(card) {
      if (reduceMotion || !el.drop || !card) return;
      var thumb = card.querySelector(".pbb-thumb");
      if (!thumb) return;
      var a = thumb.getBoundingClientRect();
      var b = el.drop.getBoundingClientRect();
      if (!a.width || !b.width) return;
      var ghost = document.createElement("div");
      ghost.style.cssText =
        "position:fixed;z-index:9999;pointer-events:none;border-radius:12px;" +
        "background:#F2F2EC;border:1.5px solid #FF5F00;opacity:.95;" +
        "transition:transform 520ms cubic-bezier(.34,1.25,.64,1),opacity 520ms ease-in;" +
        "left:" + a.left + "px;top:" + a.top + "px;width:" + a.width + "px;height:" + a.height + "px";
      document.body.appendChild(ghost);
      var dx = b.left + b.width / 2 - (a.left + a.width / 2);
      var dy = b.top + 40 - (a.top + a.height / 2);
      requestAnimationFrame(function () {
        ghost.style.transform = "translate(" + dx + "px," + dy + "px) scale(.18)";
        ghost.style.opacity = "0.1";
      });
      setTimeout(function () {
        if (ghost.parentNode) ghost.parentNode.removeChild(ghost);
      }, 560);
    }

    // ---- Rendering --------------------------------------------------------
    function renderTiers() {
      if (!el.tiers) return;
      if (!ladder.length) {
        el.tiers.innerHTML = "";
        // No published ladder: never show the raw %save% placeholder.
        if (el.sub) el.sub.hidden = true;
        return;
      }
      var maxPct = 0;
      ladder.forEach(function (t) {
        if (t[1] === "p" && t[2] > maxPct) maxPct = t[2];
      });
      if (el.sub) {
        el.sub.hidden = false;
        var tpl = el.sub.getAttribute("data-template") || "";
        // %save%, not {{ save }}: Shopify rejects double braces in a setting
        // value as an invalid dynamic source.
        el.sub.textContent = tpl.replace(/%save%/g, String(maxPct));
      }

      var current = tierFor(count() + cartQty);
      var html = "";
      ladder.forEach(function (t, i) {
        var qty = t[0];
        var gross = topPrice * qty;
        var net = gross - discountCents(gross, t);
        var isOn = current && current[0] === t[0];
        var isBest = i === ladder.length - 1;
        var per = qty > 0 ? Math.round(net / qty) : 0;
        html +=
          '<li class="pbb-tier' + (isOn ? " is-on" : "") + '" data-pbb-tier-qty="' + qty + '">' +
          (isBest ? '<span class="pbb-tier-badge">' + esc(S.bestBadge || "Najvýhodnejšie") + "</span>" : "") +
          '<button type="button" class="pbb-tier-btn">' +
          '<span class="pbb-tier-qty">' + qty + "&nbsp;ks" +
          '<span class="pbb-tier-check" aria-hidden="true">&#10003;</span></span>' +
          '<span class="pbb-tier-label">' + (t[1] === "p" ? "-" + t[2] + " %" : "") + "</span>" +
          '<span class="pbb-tier-price">' + (mixedPricing ? "od " : "") + money(net, moneyFormat) + "</span>" +
          '<span class="pbb-tier-per">' + money(per, moneyFormat) + "/ks</span>" +
          '<span class="pbb-tier-pills">' +
          (t[1] === "p" ? '<span class="pbb-pill pbb-pill--save">' + esc(S.text_110) + '' + t[2] + " %</span>" : "") +
          "</span>" +
          "</button></li>";
      });
      el.tiers.innerHTML = html;
    }

    function esc(s) {
      return String(s == null ? "" : s).replace(/[&<>"']/g, function (c) {
        return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c];
      });
    }

    function renderCard(card) {
      var vid = card.getAttribute("data-vid");
      var qty = sel[vid] || 0;
      var add = card.querySelector("[data-pbb-add]");
      var stepper = card.querySelector("[data-pbb-stepper]");
      var chip = card.querySelector("[data-pbb-chip]");
      var countEl = card.querySelector("[data-pbb-count]");
      if (add) add.hidden = qty > 0;
      if (stepper) stepper.hidden = qty === 0;
      if (countEl) countEl.textContent = qty + "×";
      if (chip) {
        chip.hidden = qty === 0;
        chip.textContent = qty + "×";
      }
      card.classList.toggle("is-in", qty > 0);
    }

    function renderChosen() {
      var total = count();
      var html = "";
      if (!total) {
        html = '<li class="pbb-empty">' + esc(S.empty || "") + "</li>";
      } else {
        order.forEach(function (vid) {
          var qty = sel[vid];
          if (!qty) return;
          var card = byId[vid];
          if (!card) return;
          var img = card.getAttribute("data-img") || "";
          html +=
            '<li class="pbb-chosen-tile" data-vid="' + esc(vid) + '" title="' + esc(card.getAttribute("data-name")) + '">' +
            (img
              ? '<img src="' + esc(img) + '" alt="' + esc(card.getAttribute("data-name")) + '" loading="lazy">'
              : '<span class="pbb-tile-noimg">' + esc((card.getAttribute("data-name") || "?").charAt(0)) + "</span>") +
            '<span class="pbb-tile-qty">' + qty + "×</span>" +
            '<button type="button" class="pbb-tile-x" data-pbb-remove aria-label="' + esc(S.text_117) + '">&times;</button>' +
            "</li>";
        });
      }
      chosenLists.forEach(function (list) {
        list.classList.toggle("pbb-chosen--grid", total > 0);
        list.innerHTML = html;
      });
    }

    function render() {
      var total = count();
      var gross = subtotalCents();
      var tier = tierFor(total + cartQty);
      var off = discountCents(gross, tier);
      var net = gross - off;

      renderChosen();
      renderTiers();

      if (el.counter) {
        el.counter.textContent = total + " / " + maxQty;
      }
      if (el.progress) {
        // Paired with transform-origin:left in the CSS; scaling beats width.
        el.progress.style.transform =
          "scaleX(" + Math.min(1, total / maxQty).toFixed(4) + ")";
      }
      if (el.hint) {
        var hintText = nextStepHint(total + cartQty);
        if (cartQty > 0) {
          hintText =
            S.text_111 + cartQty + " " + plural(cartQty, S.pcs || []) +
            (hintText ? " " + hintText : "");
        }
        el.hint.textContent = hintText;
      }
      totalNodes.forEach(function (node) {
        node.textContent = money(net, moneyFormat);
      });
      saveRows.forEach(function (row) {
        row.hidden = off <= 0;
        var label = row.querySelector("[data-pbb-save-label]");
        var value = row.querySelector("[data-pbb-save]");
        if (label) {
          label.textContent =
            (S.savedVs || "Ušetríš oproti kúpe jednotlivo") +
            (tier && tier[1] === "p" ? " (-" + tier[2] + " %)" : "");
        }
        if (value) value.textContent = "-" + money(off, moneyFormat);
      });
      if (el.barSave) {
        el.barSave.textContent = off > 0 ? S.text_110 + money(off, moneyFormat) : "";
      }
      submits.forEach(function (button) {
        button.disabled = total === 0;
      });
      if (el.bar) {
        el.bar.hidden = total === 0;
        if (el.barCount) {
          el.barCount.textContent =
            total + " " + plural(total, S.pcs || []);
        }
        if (el.barTotal) el.barTotal.textContent = money(net, moneyFormat);
      }
    }

    function nextStepHint(total) {
      if (!ladder.length) return "";
      for (var i = 0; i < ladder.length; i++) {
        if (total < ladder[i][0]) {
          var need = ladder[i][0] - total;
          var pct = ladder[i][1] === "p" ? ladder[i][2] + " %" : "";
          return (
            S.text_112 + need + " " + plural(need, S.animals || []) +
            (pct ? S.text_113 + pct + S.text_114 : ".")
          );
        }
      }
      return "";
    }

    // ---- Mutations --------------------------------------------------------
    function add(vid, silent) {
      var card = byId[vid];
      if (!card || card.getAttribute("data-sold-out")) return false;
      if (count() >= maxQty) {
        if (!silent) toast(S.maxed);
        return false;
      }
      var before = tierFor(count() + cartQty);
      sel[vid] = (sel[vid] || 0) + 1;
      if (order.indexOf(vid) === -1) order.push(vid);
      var after = tierFor(count() + cartQty);
      renderCard(card);
      render();
      if (!silent) {
        fly(card);
        if (after && (!before || after[0] !== before[0])) {
          toast(
            (S.upgraded || "") +
              (after[1] === "p" ? " → -" + after[2] + " %" : ""),
          );
        }
      }
      return true;
    }

    function dec(vid) {
      if (!sel[vid]) return;
      sel[vid] -= 1;
      if (sel[vid] <= 0) {
        delete sel[vid];
        order = order.filter(function (x) {
          return x !== vid;
        });
      }
      if (byId[vid]) renderCard(byId[vid]);
      render();
    }

    function remove(vid) {
      if (!sel[vid]) return;
      delete sel[vid];
      order = order.filter(function (x) {
        return x !== vid;
      });
      if (byId[vid]) renderCard(byId[vid]);
      render();
    }

    // ---- Filtering --------------------------------------------------------
    var CATEGORY_ORDER = [
      "Psy",
      "Mačky",
      "Vtáky",
      "Morské",
      "Zvieratká",
      "Fantázia",
      "Dekorácie",
      "Vianoce",
      "Halloween",
      "Veľká noc",
      "Valentín",
    ];

    function buildChips() {
      if (!el.chips) return;
      var present = Object.create(null);
      var hasFlexi = false;
      cards.forEach(function (card) {
        present[card.getAttribute("data-cat")] = true;
        if (card.getAttribute("data-flexi") === "1") hasFlexi = true;
      });
      var list = [{ key: "*", label: S.text_115 }];
      CATEGORY_ORDER.forEach(function (cat) {
        if (present[cat]) list.push({ key: cat, label: (S.categoryLabels && S.categoryLabels[cat]) || cat });
      });
      Object.keys(present).forEach(function (cat) {
        if (CATEGORY_ORDER.indexOf(cat) === -1) list.push({ key: cat, label: (S.categoryLabels && S.categoryLabels[cat]) || cat });
      });
      if (hasFlexi) list.push({ key: "@flexi", label: S.text_116 });

      el.chips.innerHTML = list
        .map(function (c) {
          return (
            '<button type="button" class="pbb-chip' +
            (c.key === filter ? " is-on" : "") +
            '" data-pbb-chip-key="' + esc(c.key) + '" aria-pressed="' +
            (c.key === filter) + '">' + esc(c.label) + "</button>"
          );
        })
        .join("");
    }

    function matches(card) {
      if (filter === "@flexi") {
        if (card.getAttribute("data-flexi") !== "1") return false;
      } else if (filter !== "*" && card.getAttribute("data-cat") !== filter) {
        return false;
      }
      if (!query) return true;
      return (card.getAttribute("data-search") || "").indexOf(query) !== -1;
    }

    function applyFilter() {
      var shown = 0;
      cards.forEach(function (card) {
        var ok = matches(card);
        card.hidden = !ok;
        if (ok) shown++;
      });
      if (el.gridCount) {
        el.gridCount.textContent = shown + " " + plural(shown, S.pcs || []);
      }
      if (el.noResults) el.noResults.hidden = shown > 0;
      if (el.chips) {
        [].forEach.call(el.chips.children, function (chip) {
          var on = chip.getAttribute("data-pbb-chip-key") === filter;
          chip.classList.toggle("is-on", on);
          chip.setAttribute("aria-pressed", String(on));
        });
      }
    }

    // ---- Bulk actions -----------------------------------------------------
    function mysteryFill() {
      var tier = null;
      // Fill up to the next ladder step (counting what is already in the cart),
      // or to max when the top step is reached.
      for (var i = 0; i < ladder.length; i++) {
        if (count() + cartQty < ladder[i][0]) {
          tier = ladder[i];
          break;
        }
      }
      var target = tier ? tier[0] - cartQty : maxQty;
      var need = Math.min(target, maxQty) - count();
      if (need <= 0) {
        toast(S.maxed);
        return;
      }
      var pool = cards.filter(function (card) {
        return !card.getAttribute("data-sold-out") && !sel[card.getAttribute("data-vid")];
      });
      var added = 0;
      while (added < need && pool.length) {
        var idx = Math.floor(Math.random() * pool.length);
        var pick = pool.splice(idx, 1)[0];
        if (add(pick.getAttribute("data-vid"), true)) added++;
      }
      if (added) {
        toast(
          (S.mysteryDone || "") + " " + added + " " + plural(added, S.animals || []),
        );
      }
    }

    function addWholeCategory() {
      if (filter === "*" || filter === "@flexi") {
        toast(S.pickCategory);
        return;
      }
      var room = maxQty - count();
      if (room <= 0) {
        toast(S.maxed);
        return;
      }
      var pool = cards.filter(function (card) {
        return (
          card.getAttribute("data-cat") === filter &&
          !card.getAttribute("data-sold-out") &&
          !sel[card.getAttribute("data-vid")]
        );
      });
      if (!pool.length) {
        toast(S.categoryIn);
        return;
      }
      var added = 0;
      for (var i = 0; i < pool.length && added < room; i++) {
        if (add(pool[i].getAttribute("data-vid"), true)) added++;
      }
      if (added) {
        toast("+" + added + " " + plural(added, S.animals || []));
      }
    }

    // ---- Cart -------------------------------------------------------------
    var submitting = false;

    function submit() {
      if (submitting || !count()) return;
      var items = order
        .filter(function (vid) {
          return sel[vid] > 0;
        })
        .map(function (vid) {
          return { id: Number(vid), quantity: sel[vid] };
        });
      if (!items.length) return;

      var additionConfirmed = false, responseStatus;
      var errorEl = root.querySelector('[data-pbb-error]');
      if (errorEl) errorEl.hidden = true;
      function showFailure() {
        if (!errorEl) { errorEl = document.createElement('p'); errorEl.setAttribute('data-pbb-error', ''); errorEl.setAttribute('role', 'alert'); root.appendChild(errorEl); }
        errorEl.hidden = false;
        errorEl.textContent = additionConfirmed ? 'Položky sú v košíku, ale jeho zobrazenie sa nepodarilo obnoviť. Otvorte košík a skontrolujte obsah.' : 'Pridanie sa nepodarilo potvrdiť. Pred opakovaním skontrolujte košík.';
        if (window.PrinthiaErrors) window.PrinthiaErrors.report({code:additionConfirmed?'cart_display_failed':'cart_write_failed',flow:additionConfirmed?'storefront':'cart',endpoint:addUrl+'.js',status:responseStatus>=400?responseStatus:undefined,show:false});
      }

      submitting = true;
      var labels = submits.map(function (b) {
        return b.textContent;
      });
      submits.forEach(function (b) {
        b.disabled = true;
        b.textContent = S.adding || "…";
      });

      // window.fetch on purpose, resolved at call time: pb-cart.js wraps it and
      // opens the drawer when a /cart/add call succeeds. The sections param asks
      // Shopify to return fresh cart-drawer + icon-bubble HTML in the same round
      // trip, so the THEME drawer can open with real contents when the
      // PrintBundles drawer is not on the page.
      window
        .fetch(addUrl + ".js", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({
            items: items,
            sections: "cart-drawer,cart-icon-bubble",
          }),
        })
        .then(function (res) {
          responseStatus = res.status;
          if (!res.ok) throw new Error("cart add failed: " + res.status);
          return res.json();
        })
        .then(function (data) {
          var lines=data&&Array.isArray(data.items)?data.items:data&&data.id?[data]:[];
          if (!data || data.error || data.ok===false || data.success===false || data.success==='false' || data.status==='error' || data.status==='fail' || (data.errors&&Object.keys(data.errors).length) || Number(data.status)>=400 || !items.every(function(item){return lines.some(function(line){return Number(line.variant_id||line.id)===Number(item.id)&&Number(line.quantity)>=Number(item.quantity);});})) throw new Error('Cart result unconfirmed');
          additionConfirmed = true;
          sel = Object.create(null);
          order = [];
          cards.forEach(renderCard);
          render();
          closeSheet();
          afterAdd(data);
        })
        .catch(function () {
          showFailure();
        })
        .then(function () {
          submitting = false;
          submits.forEach(function (b, i) {
            b.textContent = labels[i];
          });
          render();
        });
    }

    // ---- Post-add feedback --------------------------------------------------
    // The PrintBundles drawer (pb-cart.js) wraps window.fetch and opens itself
    // on a successful /cart/add. When that drawer is not on the page (config
    // unpublished), the customer still needs feedback without a reload: update
    // the header cart bubble from /cart.js and show a toast with a cart link.
    function updateBubble(n) {
      try {
        var holder = document.getElementById("cart-icon-bubble");
        var bubble = document.querySelector(".cart-count-bubble");
        if (!bubble && holder && n > 0) {
          bubble = document.createElement("div");
          bubble.className = "cart-count-bubble";
          bubble.innerHTML = '<span aria-hidden="true"></span>';
          holder.appendChild(bubble);
        }
        if (bubble) {
          var span = bubble.querySelector("span[aria-hidden]");
          if (span) span.textContent = n;
          else bubble.textContent = n;
          bubble.classList.remove("pthia-pulse");
          void bubble.offsetWidth;
          bubble.classList.add("pthia-pulse");
        }
      } catch (e) {
        /* feedback only, never breaks the add */
      }
    }

    function afterAdd(data) {
      // Keep the ladder honest: what was just added now counts as cart quantity.
      syncCartQty();

      // 1. PrintBundles drawer on the page: its fetch wrapper already opened it.
      if (document.querySelector("[data-pbc-root]")) {
        toast(S.added);
        return;
      }

      // 2. Theme drawer: hand it the fresh sections and let it open itself with
      //    its own animation (Dawn/Refresh cart-drawer.renderContents).
      var sections = data && data.sections ? data.sections : null;
      var drawer = document.querySelector("cart-drawer");
      if (
        drawer &&
        sections &&
        sections["cart-drawer"] &&
        typeof drawer.renderContents === "function"
      ) {
        try {
          drawer.renderContents({ sections: sections });
          toast(S.added);
          return;
        } catch (e) {
          /* fall through to the link toast */
        }
      }

      // 3. No drawer at all: refresh the header icon and give a clickable path.
      if (sections && sections["cart-icon-bubble"]) {
        try {
          var probe = document.createElement("div");
          probe.innerHTML = sections["cart-icon-bubble"];
          var freshIcon = probe.querySelector("#cart-icon-bubble");
          var liveIcon = document.getElementById("cart-icon-bubble");
          if (freshIcon && liveIcon) liveIcon.innerHTML = freshIcon.innerHTML;
        } catch (e) {
          /* bubble update via /cart.js still happens in syncCartQty */
        }
      }
      if (el.toast) {
        clearTimeout(toastTimer);
        el.toast.innerHTML =
          esc(S.added || "") +
          ' <a href="' + esc(root.getAttribute("data-cart-url") || "/cart") + '">' +
          esc(S.viewCart || "Zobraziť košík") + "</a>";
        el.toast.hidden = false;
        el.toast.style.animation = "none";
        void el.toast.offsetWidth;
        el.toast.style.animation = "";
        toastTimer = setTimeout(function () {
          el.toast.hidden = true;
        }, 6000);
      }
    }

    // ---- Mobile sheet -----------------------------------------------------
    function openSheet() {
      if (!el.sheet) return;
      el.sheet.hidden = false;
      document.documentElement.style.overflow = "hidden";
    }

    function closeSheet() {
      if (!el.sheet) return;
      el.sheet.hidden = true;
      document.documentElement.style.overflow = "";
    }

    // ---- Wiring -----------------------------------------------------------
    root.addEventListener("click", function (event) {
      var target = event.target;
      if (!target || !target.closest) return;

      var chip = target.closest("[data-pbb-chip-key]");
      if (chip && root.contains(chip)) {
        filter = chip.getAttribute("data-pbb-chip-key");
        applyFilter();
        return;
      }

      var tierBtn = target.closest("[data-pbb-tier-qty]");
      if (tierBtn && root.contains(tierBtn)) {
        var want = parseInt(tierBtn.getAttribute("data-pbb-tier-qty"), 10) || 0;
        if (count() < want) mysteryFill();
        return;
      }

      var card = target.closest("[data-pbb-card]");
      if (card && root.contains(card)) {
        var vid = card.getAttribute("data-vid");
        if (target.closest("[data-pbb-add]") || target.closest("[data-pbb-inc]")) {
          event.preventDefault();
          add(vid);
          return;
        }
        if (target.closest("[data-pbb-dec]")) {
          event.preventDefault();
          dec(vid);
          return;
        }
      }

      var removeBtn = target.closest("[data-pbb-remove]");
      if (removeBtn) {
        var row = removeBtn.closest("[data-vid]");
        if (row) remove(row.getAttribute("data-vid"));
        return;
      }

      if (target.closest("[data-pbb-submit]")) {
        submit();
        return;
      }
      if (target.closest("[data-pbb-mystery]")) {
        mysteryFill();
        return;
      }
      if (target.closest("[data-pbb-add-category]")) {
        addWholeCategory();
        return;
      }
      if (target.closest("[data-pbb-sheet-open]")) {
        openSheet();
        return;
      }
      if (target.closest("[data-pbb-sheet-close]")) {
        closeSheet();
      }
    });

    document.addEventListener("keydown", function (event) {
      if (event.key === "Escape" && el.sheet && !el.sheet.hidden) closeSheet();
    });

    if (el.search) {
      var searchTimer = null;
      el.search.addEventListener("input", function () {
        clearTimeout(searchTimer);
        searchTimer = setTimeout(function () {
          query = (el.search.value || "").trim().toLowerCase();
          applyFilter();
        }, 140);
      });
    }

    if (!ladder.length && el.mystery) el.mystery.hidden = true;
    if (!grouped && ladder.length) {
      // Bundle mode is off on the covering offer, so the function would count
      // per product and a mixed bundle would get nothing. Say so in the console
      // rather than quietly promising a discount the cart will not honour.
      if (window.console && window.console.warn) {
        window.console.warn(
          "[pb-bundle] The PrintBundles offer for this collection does not have " +
            "bundle mode on, so mixed bundles will not be discounted at checkout.",
        );
      }
    }

    buildChips();
    applyFilter();
    cards.forEach(renderCard);
    render();
    syncCartQty();
  }

  function boot() {
    [].forEach.call(document.querySelectorAll("[data-pbb]"), init);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }

  // Theme editor: sections are re-rendered without a page load.
  document.addEventListener("shopify:section:load", function (event) {
    if (!event.target || !event.target.querySelectorAll) return;
    [].forEach.call(event.target.querySelectorAll("[data-pbb]"), init);
  });
})();
